// client/src/components/Vehicles/VehicleHistoryModal.jsx
import React from 'react';
import {
  Modal,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot,
} from '@mui/lab';
import {
  Close as CloseIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';
import { formatDateOnly } from '../../utils/dateUtils';
import { useAuth } from '../../hooks/useAuth';

const fetchVehicleHistory = async (id) => {
  const { data } = await axios.get(`/vehicles/${id}/history`);
  return data;
};

const getIcon = (type, status) => {
  if (type === 'work_order') {
    if (status === 'completed') return <CheckCircleIcon />;
    if (status === 'in-progress') return <BuildIcon />;
    if (status === 'pending') return <PendingIcon />;
    return <AssignmentIcon />;
  }
  if (type === 'device_install') return <SettingsIcon />;
  if (type === 'vehicle_change') return <EditIcon />;
  return <AssignmentIcon />;
};

const getColor = (type, status) => {
  if (type === 'work_order') {
    if (status === 'completed') return 'success';
    if (status === 'in-progress') return 'warning';
    if (status === 'pending') return 'info';
    if (status === 'cancelled') return 'error';
    return 'info';
  }
  if (type === 'device_install') {
    if (status === 'active') return 'success';
    if (status === 'removed') return 'error';
    return 'primary';
  }
  if (type === 'vehicle_change') return 'secondary';
  if (type === 'ticket') {
    if (status === 'resolved') return 'success';
    if (status === 'closed') return 'default';
    return 'error';
  }
  return 'primary';
};

const getStatusLabel = (status) => {
  if (!status) return null;
  const labels = {
    'pending': 'Pending',
    'in-progress': 'In Progress',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
    'active': 'Active',
    'removed': 'Removed',
    'open': 'Open',
    'resolved': 'Resolved',
    'closed': 'Closed'
  };
  return labels[status] || status;
};

const getEventTitle = (event) => {
  if (event.type === 'vehicle_change') {
    switch (event.data?.field) {
      case 'licensePlate': return 'License Plate Changed';
      case 'vin': return 'VIN Changed';
      case 'brand': return 'Brand Changed';
      case 'model': return 'Model Changed';
      case 'year': return 'Year Changed';
      case 'clientId': return 'Client Changed';
      case 'isActive': return event.newValue === true ? 'Vehicle Activated' : 'Vehicle Deactivated';
      default: return event.title || 'Vehicle Information Changed';
    }
  }
  return event.title;
};

const getEventDescription = (event) => {
  if (event.type === 'vehicle_change') {
    const { field, oldValue, newValue, notes } = event.data || {};
    if (field === 'isActive') {
      return notes || `Status changed to ${newValue ? 'active' : 'inactive'}`;
    }
    return `Changed from "${oldValue || 'empty'}" to "${newValue || 'empty'}"${notes ? `. ${notes}` : ''}`;
  }
  return event.description;
};

const VehicleHistoryModal = ({ open, vehicleId, onClose }) => {
  const { user } = useAuth();
  const { data: timeline, isLoading, error } = useQuery({
    queryKey: ['vehicleHistory', vehicleId],
    queryFn: () => fetchVehicleHistory(vehicleId),
    enabled: !!vehicleId && open,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Paper
        sx={{
          width: '90%',
          maxWidth: 1000,
          height: '80%',
          overflow: 'auto',
          position: 'relative',
          p: 3,
          borderRadius: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" gutterBottom fontWeight={600}>
          Vehicle History Timeline
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Complete history of work orders, device installations, vehicle changes, and tickets
        </Typography>

        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error">
              Error loading history: {error.message}
            </Typography>
          </Box>
        )}

        {timeline && timeline.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="textSecondary" gutterBottom>
              No history found for this vehicle.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              History will appear when work orders are completed, devices are installed, or vehicle information is changed.
            </Typography>
          </Box>
        )}

        {timeline && timeline.length > 0 && (
          <Timeline position="alternate">
            {timeline.map((event, idx) => {
              const eventDate = event.date;
              const eventType = event.type;
              const eventStatus = event.status;
              
              return (
                <TimelineItem key={event.id || idx}>
                  <TimelineOppositeContent color="textSecondary">
                    {formatDateOnly(
                      eventDate,
                      user?.settings?.timezone || 'UTC',
                      user?.settings?.dateFormat || 'dd/MM/yyyy'
                    )}
                    <Typography variant="caption" display="block" color="textSecondary">
                      {eventDate && new Date(eventDate).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </TimelineOppositeContent>
                  
                  <TimelineSeparator>
                    <TimelineDot color={getColor(eventType, eventStatus)}>
                      {getIcon(eventType, eventStatus)}
                    </TimelineDot>
                    {idx < timeline.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  
                  <TimelineContent>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {getEventTitle(event)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {getEventDescription(event)}
                      </Typography>
                      
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={eventType?.replace('_', ' ').toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                        {eventStatus && (
                          <Chip
                            label={getStatusLabel(eventStatus).toUpperCase()}
                            size="small"
                            color={getColor(eventType, eventStatus)}
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                      
                      {event.data && (
                        <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="textSecondary">
                            {event.data.mechanic && `Mechanic: ${event.data.mechanic}`}
                            {event.data.serialNumber && ` | Device: ${event.data.serialNumber}`}
                            {event.data.type && ` | Type: ${event.data.type}`}
                            {event.data.changedBy && ` | Changed by: ${event.data.changedBy}`}
                            {event.data.field && ` | Field: ${event.data.field}`}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Paper>
    </Modal>
  );
};

export default VehicleHistoryModal;